import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ImLinkedin2, ImFacebook } from "react-icons/im";
import { BsYoutube } from "react-icons/bs";
import { FaInstagramSquare, FaWhatsapp } from "react-icons/fa";
import {
  FiPhoneCall,
  FiMail,
  FiMapPin,
  FiArrowRight,
  FiSend,
} from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sendContactEmail } from "../../emailService";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const form = useRef();

  const handleNewsletter = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await sendContactEmail(form.current);
      toast.success("Subscribed successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "dark",
      });
      setEmail("");
    } catch (error) {
      console.error("Newsletter submission failed:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
        autoClose: 2500,
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  const services = [
    { label: "Digital Marketing", to: "/digital-marketing" },
    { label: "Website Development", to: "/website-development" },
    { label: "SEO Services", to: "/search-engine-optimization" },
    { label: "Mobile Apps", to: "/mobile-application-development" },
    { label: "Software Development", to: "/software-development" },
    { label: "Social Media", to: "/social-media-marketing" },
  ];

  const company = [
    { label: "Home", to: "/" },
    { label: "About", to: "/aboutus" },
    { label: "Our Work", to: "/CaseStudies" },
    { label: "Teams", to: "/teams" },
    { label: "Blog", to: "/blog" },
    { label: "FAQs", to: "/faq" },
    { label: "Contact", to: "/contactus" },
  ];

  const socials = [
    { icon: ImLinkedin2, href: "https://www.linkedin.com/company/abscod-informatics/?original_referer=", color: "hover:bg-[#0077B5]" },
    { icon: ImFacebook, href: "https://www.facebook.com/abscod.informatics/", color: "hover:bg-[#3b5998]" },
    { icon: BsYoutube, href: "https://www.youtube.com/@abscodinformatics", color: "hover:bg-[#c4302b]" },
    { icon: FaInstagramSquare, href: "https://www.instagram.com/abscod.informatics/", color: "hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#e6683c] hover:to-[#bc2a8d]" },
    { icon: FaWhatsapp, href: "https://wa.me/918630062102", color: "hover:bg-[#25d366]" },
  ];

  const navHeadingClass =
    "mb-5 font-raleway text-sm font-semibold uppercase tracking-widest text-white/90";

  return (
    <footer className="relative overflow-hidden bg-[#050811] text-white">
      <ToastContainer newestOnTop closeOnClick pauseOnHover theme="dark" />
      {/* Top gradient line */}
      <div className="absolute top-0 left-0 right-0 z-20 h-1 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#c084fc]" />

      {/* Decorative glows */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-80 w-[120%] rounded-full bg-[#3b82f6]/10 blur-[130px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-96 w-96 rounded-full bg-[#c084fc]/5 blur-[110px]" />

      {/* CTA card */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 py-16">
        <div className="p-[1px] rounded-[2rem] bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#c084fc]">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#0f1621] to-[#151d29] px-8 py-14 text-center md:px-16 md:py-20">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/10 via-transparent to-[#c084fc]/10" />

            <h2 className="relative font-raleway text-3xl font-extrabold leading-tight md:text-4xl lg:text-5xl">
              <span className="text-white">Ready to scale your </span>
              <span className="text-[#FFDB16] drop-shadow-[0_0_20px_rgba(255,219,22,0.4)]">
                business with us?
              </span>
            </h2>

            <p className="relative mx-auto mt-4 max-w-2xl font-openSans text-base text-white/70 md:text-lg">
              Let's build something extraordinary together. Start with a free
              discovery call today.
            </p>

            <div className="relative mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contactus"
                className="group inline-flex items-center gap-2 rounded-full bg-[#FFDB16] px-7 py-3.5 text-sm sm:text-base font-semibold text-slate-950 shadow-[0_12px_40px_rgba(255,219,22,0.25)] transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_50px_rgba(255,219,22,0.35)]"
              >
                Book a discovery call
                <FiArrowRight className="transition group-hover:translate-x-1" />
              </Link>
              <a
                href="mailto:info@abscod.com"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-7 py-3.5 text-sm sm:text-base font-semibold text-white backdrop-blur-sm transition hover:bg-white/10"
              >
                Email us
                <FiMail />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12">
            {/* Brand */}
            <div className="lg:col-span-4">
              <Link to="/" className="inline-block">
                <img
                  src="/Images/abscodwhite.webp"
                  alt="Abscod Informatics"
                  width="150"
                  height="60"
                  className="object-contain"
                />
              </Link>
              <p className="font-openSans mt-5 max-w-sm text-sm leading-relaxed text-white/60">
                We help businesses scale with digital transformation, cloud ERP,
                and data-driven marketing that delivers measurable growth.
              </p>

              {/* Newsletter */}
              <form
                ref={form}
                onSubmit={handleNewsletter}
                className="mt-6 flex max-w-sm items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1.5 backdrop-blur-sm"
              >
                <input
                  type="email"
                  name="user_email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 bg-transparent px-4 py-2 text-sm text-white placeholder-white/40 outline-none"
                />
                <input type="hidden" name="user_name" value="Newsletter Subscriber" />
                <input type="hidden" name="user_phone" value="N/A" />
                <input type="hidden" name="user_project" value="Newsletter subscription request" />
                <button
                  type="submit"
                  disabled={loading}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFDB16] text-slate-950 transition hover:bg-white disabled:opacity-60"
                  aria-label="Subscribe"
                >
                  <FiSend />
                </button>
              </form>

              {/* Socials */}
              <div className="mt-6 flex items-center gap-3">
                {socials.map(({ icon: Icon, href, color }, i) => (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className={`flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 transition hover:scale-110 hover:text-white ${color}`}
                    aria-label="Social link"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="lg:col-span-2">
              <h4 className={navHeadingClass}>Services</h4>
              <ul className="space-y-3">
                {services.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      className="group flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8b5cf6] opacity-0 transition group-hover:opacity-100" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div className="lg:col-span-3">
              <h4 className={navHeadingClass}>Company</h4>
              <ul className="space-y-3">
                {company.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      className="group flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8b5cf6] opacity-0 transition group-hover:opacity-100" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="lg:col-span-3">
              <h4 className={navHeadingClass}>Contact</h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="tel:+918630062102"
                    className="flex items-center gap-3 text-sm text-white/70 transition hover:text-white"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                      <FiPhoneCall />
                    </span>
                    +91 8630062102
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+919560438136"
                    className="flex items-center gap-3 text-sm text-white/70 transition hover:text-white"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                      <FiPhoneCall />
                    </span>
                    +91 9560438136
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@abscod.com"
                    className="flex items-center gap-3 text-sm text-white/70 transition hover:text-white"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5">
                      <FiMail />
                    </span>
                    info@abscod.com
                  </a>
                </li>
                <li>
                  <span className="flex items-start gap-3 text-sm text-white/70">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
                      <FiMapPin />
                    </span>
                    Plot 44, Sector 5, Rajendra Nagar, Ghaziabad, UP, 201005, India
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative z-10 border-t border-white/5 bg-[#03050a]">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm text-white/50">
              Copyright 2026 Abscod Informatics. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/Privacy" className="transition hover:text-white">
                Privacy Policy
              </Link>
              <Link to="/TermsAndConditions" className="transition hover:text-white">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
